/**
 * Date-range presets for the Inspections listing — matches legacy Kendo RANGE_OPTIONS order.
 *
 * These presets use Sunday as the week start to match the backend WEEK_START=:sunday
 * setting and the legacy moment.js behaviour.
 *
 * The Custom preset button id is 'custom' (CUSTOM_DATE_RANGE_PRESET_ID) as required by
 * DateRangeFilter's custom-mode detection. State stores this preset as rangeType='custom_range'.
 */
import I18n from '@utils/i18n';
/** Button id for the custom-date-entry preset — matches DateRangeFilter's internal constant. */
export const CUSTOM_DATE_RANGE_PRESET_ID = 'custom';
/** State-level rangeType for user-entered dates (calendar mode). */
export const CUSTOM_RANGE_TYPE = 'custom_range';
// ---------------------------------------------------------------------------
// I18n label helper
// ---------------------------------------------------------------------------
function rangeOptionLabel(id, fallback) {
    try {
        const fn = I18n === null || I18n === void 0 ? void 0 : I18n.t;
        if (typeof fn !== 'function')
            return fallback;
        const translated = fn.call(I18n, `datatables.kendo.range_options.${id}`);
        if (!translated || translated.startsWith('[missing'))
            return fallback;
        return translated;
    }
    catch (_a) {
        return fallback;
    }
}
// ---------------------------------------------------------------------------
// Week helpers — Sunday start
// ---------------------------------------------------------------------------
/** Start of the Sunday-based week that contains `d`. */
function startOfSundayWeek(d) {
    const day = d.getDay(); // 0 = Sunday
    return new Date(d.getFullYear(), d.getMonth(), d.getDate() - day);
}
/** End of the Sunday-based week that contains `d`. */
function endOfSundayWeek(d) {
    const day = d.getDay();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate() + (6 - day));
}
// ---------------------------------------------------------------------------
// Quarter helpers — calendar quarters (Q1=Jan–Mar, Q2=Apr–Jun, …)
// ---------------------------------------------------------------------------
function quarterStartDate(year, month) {
    const q = Math.floor(month / 3) * 3;
    return new Date(year, q, 1);
}
function quarterEndDate(year, month) {
    const q = Math.floor(month / 3) * 3;
    return new Date(year, q + 3, 0); // day 0 of month (q+3) = last day of month (q+2)
}
// ---------------------------------------------------------------------------
// Range compute functions — return DateRange (Date objects)
// ---------------------------------------------------------------------------
function thisWeekRange() {
    const today = new Date();
    return { start: startOfSundayWeek(today), end: endOfSundayWeek(today) };
}
function nextWeekRange() {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return { start: startOfSundayWeek(d), end: endOfSundayWeek(d) };
}
function lastWeekRange() {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return { start: startOfSundayWeek(d), end: endOfSundayWeek(d) };
}
function thisMonthRange() {
    const now = new Date();
    return {
        start: new Date(now.getFullYear(), now.getMonth(), 1),
        end: new Date(now.getFullYear(), now.getMonth() + 1, 0)
    };
}
function nextMonthRange() {
    const now = new Date();
    return {
        start: new Date(now.getFullYear(), now.getMonth() + 1, 1),
        end: new Date(now.getFullYear(), now.getMonth() + 2, 0)
    };
}
function lastMonthRange() {
    const now = new Date();
    return {
        start: new Date(now.getFullYear(), now.getMonth() - 1, 1),
        end: new Date(now.getFullYear(), now.getMonth(), 0)
    };
}
function thisQuarterRange() {
    const now = new Date();
    return {
        start: quarterStartDate(now.getFullYear(), now.getMonth()),
        end: quarterEndDate(now.getFullYear(), now.getMonth())
    };
}
function nextQuarterRange() {
    const now = new Date();
    const d = new Date(now.getFullYear(), now.getMonth() + 3, 1);
    return {
        start: quarterStartDate(d.getFullYear(), d.getMonth()),
        end: quarterEndDate(d.getFullYear(), d.getMonth())
    };
}
function lastQuarterRange() {
    const now = new Date();
    const d = new Date(now.getFullYear(), now.getMonth() - 3, 1);
    return {
        start: quarterStartDate(d.getFullYear(), d.getMonth()),
        end: quarterEndDate(d.getFullYear(), d.getMonth())
    };
}
function thisYearRange() {
    const now = new Date();
    return {
        start: new Date(now.getFullYear(), 0, 1),
        end: new Date(now.getFullYear(), 11, 31)
    };
}
function lastYearRange() {
    const now = new Date();
    return {
        start: new Date(now.getFullYear() - 1, 0, 1),
        end: new Date(now.getFullYear() - 1, 11, 31)
    };
}
// ---------------------------------------------------------------------------
// Exported presets array
// ---------------------------------------------------------------------------
/**
 * Inspection date-range presets.
 *
 * Order mirrors legacy Kendo RANGE_OPTIONS:
 * this_week, next_week, last_week, this_month, next_month, last_month,
 * this_quarter, next_quarter, last_quarter, this_year, last_year, custom.
 *
 * The 'custom' button id is required by DateRangeFilter for calendar-mode
 * activation. State stores the custom preset as rangeType='custom_range'.
 */
export const INSPECTIONS_DATE_RANGE_PRESETS = [
    {
        id: 'this_week',
        label: rangeOptionLabel('this_week', 'This Week'),
        compute: thisWeekRange
    },
    {
        id: 'next_week',
        label: rangeOptionLabel('next_week', 'Next Week'),
        compute: nextWeekRange
    },
    {
        id: 'last_week',
        label: rangeOptionLabel('last_week', 'Last Week'),
        compute: lastWeekRange
    },
    {
        id: 'this_month',
        label: rangeOptionLabel('this_month', 'This Month'),
        compute: thisMonthRange
    },
    {
        id: 'next_month',
        label: rangeOptionLabel('next_month', 'Next Month'),
        compute: nextMonthRange
    },
    {
        id: 'last_month',
        label: rangeOptionLabel('last_month', 'Last Month'),
        compute: lastMonthRange
    },
    {
        id: 'this_quarter',
        label: rangeOptionLabel('this_quarter', 'This Quarter'),
        compute: thisQuarterRange
    },
    {
        id: 'next_quarter',
        label: rangeOptionLabel('next_quarter', 'Next Quarter'),
        compute: nextQuarterRange
    },
    {
        id: 'last_quarter',
        label: rangeOptionLabel('last_quarter', 'Last Quarter'),
        compute: lastQuarterRange
    },
    {
        id: 'this_year',
        label: rangeOptionLabel('this_year', 'This Year'),
        compute: thisYearRange
    },
    {
        id: 'last_year',
        label: rangeOptionLabel('last_year', 'Last Year'),
        compute: lastYearRange
    },
    // Custom must be last — DateRangeFilter activates calendar mode when this is clicked.
    {
        id: CUSTOM_DATE_RANGE_PRESET_ID,
        label: rangeOptionLabel('custom_range', 'Custom Range'),
        compute: () => ({ start: null, end: null })
    }
];
// ---------------------------------------------------------------------------
// Exported helpers
// ---------------------------------------------------------------------------
/** M/D/YYYY date string format — matches the backend DATE_FORMAT. */
function formatLocalDate(date) {
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
}
/**
 * Returns M/D/YYYY date strings for the given preset id.
 * Fixed presets compute dates using Sunday-start week math.
 * Returns { start: null, end: null } for 'custom', CUSTOM_RANGE_TYPE, or unknown ids.
 */
export function computeRangeForPresetId(presetId) {
    const preset = INSPECTIONS_DATE_RANGE_PRESETS.find((p) => p.id === presetId);
    if (!preset)
        return { start: null, end: null };
    const range = preset.compute();
    return {
        start: range.start ? formatLocalDate(range.start) : null,
        end: range.end ? formatLocalDate(range.end) : null
    };
}
/**
 * Maps state rangeType → filter preset button id.
 *   'custom_range' → 'custom'
 *   null           → undefined (no preset highlighted)
 *   other          → unchanged (e.g. 'this_month' → 'this_month')
 */
export function toFilterSelectedPresetId(rangeType) {
    if (!rangeType)
        return undefined;
    if (rangeType === CUSTOM_RANGE_TYPE)
        return CUSTOM_DATE_RANGE_PRESET_ID;
    return rangeType;
}
/**
 * Maps filter preset button id → state rangeType.
 *   'custom'  → 'custom_range'
 *   other     → unchanged (e.g. 'this_month' → 'this_month')
 */
export function fromFilterPresetId(presetId) {
    if (presetId === CUSTOM_DATE_RANGE_PRESET_ID)
        return CUSTOM_RANGE_TYPE;
    return presetId;
}
